import { NotificationsExternesSchema, PreferenceConversationSchema, type NotificationsExternes, type PreferenceConversation } from './notifications-preferences.js';
import type { FirebaseApp } from 'firebase/app';
import {
  collection,
  deleteDoc,
  deleteField,
  doc,
  getDoc,
  getCountFromServer,
  getDocs,
  limit as limiteDe,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  type DocumentData,
  type Firestore,
} from 'firebase/firestore';
import { connectFunctionsEmulator, getFunctions, httpsCallable } from 'firebase/functions';
import type { EmulatorEndpoint } from '@taki/runtime-config';
import { z } from 'zod';

/**
 * LE MOTEUR DE NOTIFICATIONS, cote client : les constantes et les schemas des
 * documents que l'ecran ecrit et lit. Le moteur, lui, vit dans
 * `functions/src/notifications/` (docs/NOTIFICATIONS.md).
 *
 * CE N'EST PAS UNE FAMILLE, comme le branding (`branding.ts`) : ces documents
 * vivent sous `orgs/{orgId}/`, a identifiant fixe (reglages) ou choisi
 * (regles, preferences), sans `createdAt`/`archivedAt` ni repository
 * generique. Les schemas vivent donc ici, pas dans `@taki/data-model`, pour
 * ne pas fausser le compte des familles (AGENTS.md section 7).
 *
 * LES LISTES FERMEES CI-DESSOUS SONT ECRITES DEUX FOIS, et c'est assume :
 * `functions/` ne peut dependre d'aucun `@taki/*` (docs/FUNCTIONS.md), donc
 * `functions/src/notifications/contrat.ts` les porte aussi, et
 * `packages/sdk/tests/notifications-contrat.test.mjs` lit les deux fichiers
 * et echoue si elles se separent. Chaque constante tient sur UNE ligne.
 */

/** Le document de reglages : `orgs/{orgId}/settings/notifications`. */
export const NOTIFICATIONS_SETTINGS_ID = 'notifications';
/** Les regles : `orgs/{orgId}/notificationRules/{ruleId}`. */
export const NOTIFICATION_REGLES_COLLECTION = 'notificationRules';
/** Les preferences : `orgs/{orgId}/notificationPrefs/{email}`. */
export const NOTIFICATION_PREFS_COLLECTION = 'notificationPrefs';
/** La file et le journal : `orgs/{orgId}/notifications/{id}`. */
export const NOTIFICATIONS_COLLECTION = 'notifications';
/** Le compteur mensuel : `orgs/{orgId}/notificationCompteurs/{AAAA-MM}`. */
export const NOTIFICATION_COMPTEURS_COLLECTION = 'notificationCompteurs';
/** La callable, telle que `httpsCallable` la nomme. */
export const CALLABLE_NOTIFIER = 'notifier';
/** L'auteur serveur des messages. */
export const NOTIFICATION_SERVER_ACTOR = 'system:notification';
/** Les canaux, tous. */
export const NOTIFICATION_CANAUX = ['email', 'whatsapp', 'slack', 'console', 'interne'] as const;
/** Les canaux qu'une regle ou une preference peut citer. */
export const NOTIFICATION_CANAUX_CHOISISSABLES = ['email', 'whatsapp', 'slack', 'interne'] as const;
/** Les rubriques de la boite interne (la cloche, le centre de communication). */
export const NOTIFICATION_RUBRIQUES = ['citation', 'lia', 'message', 'regle'] as const;
/** Le cycle de vie d'un message. */
export const NOTIFICATION_STATUTS = ['enFile', 'envoye', 'livre', 'echec', 'abandonne'] as const;
/** Les declencheurs d'une regle. */
export const NOTIFICATION_DECLENCHEURS = ['evenement', 'planifie', 'action'] as const;
/** Les conditions d'un declencheur `evenement`. */
export const NOTIFICATION_CONDITIONS = ['cree', 'champChange', 'statutDevient'] as const;
/** Les genres de destinataire. */
export const NOTIFICATION_KINDS_DESTINATAIRE = ['roles', 'membres', 'champ'] as const;
/** Les evenements de livraison rapportes par le fournisseur. */
export const NOTIFICATION_EVENEMENTS_LIVRAISON = ['delivered', 'bounce', 'spam', 'blocked', 'canceled'] as const;
/** Les langues. */
export const NOTIFICATION_LANGUES = ['fr', 'en', 'de', 'lb'] as const;
/** L'expediteur unique. */
export const NOTIFICATION_EXPEDITEUR_EMAIL = 'notifications@mytaki.app';
/** Les bornes d'un gabarit. */
/** Le plus de messages que `journal.periode` rend en une lecture. */
export const NOTIFICATION_PERIODE_MAX = 2000;
/** Le prefixe que le canal console pose sur `providerMessageId` (functions/src/notifications/canaux/email-console.ts). */
export const NOTIFICATION_PREFIXE_CONSOLE = 'console:';
export const NOTIFICATION_SUJET_MAX = 200;
export const NOTIFICATION_CORPS_MAX = 20000;

export type NotificationCanal = (typeof NOTIFICATION_CANAUX)[number];
export type NotificationCanalChoisissable = (typeof NOTIFICATION_CANAUX_CHOISISSABLES)[number];
export type NotificationStatut = (typeof NOTIFICATION_STATUTS)[number];
export type NotificationDeclencheur = (typeof NOTIFICATION_DECLENCHEURS)[number];
export type NotificationCondition = (typeof NOTIFICATION_CONDITIONS)[number];
export type NotificationLangue = (typeof NOTIFICATION_LANGUES)[number];
export type NotificationRubrique = (typeof NOTIFICATION_RUBRIQUES)[number];

const heure = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'HH:MM attendu');
const chemin = z.string().regex(/^[A-Za-z0-9_]+(\.[A-Za-z0-9_-]+)*$/, 'chemin de champ attendu');
const idSur = z.string().regex(/^[A-Za-z0-9_-]{1,128}$/);

/** `orgs/{orgId}/settings/notifications`. */
export const NotificationReglagesSchema = z.object({
  orgId: z.string().min(1),
  schemaVersion: z.literal(1),
  replyTo: z.string().trim().email().optional(),
  nomExpediteur: z.string().trim().min(1).max(80).optional(),
  piedDePage: z.string().trim().max(300).optional(),
  langue: z.enum(NOTIFICATION_LANGUES).optional(),
  canaux: z.object({
    email: z.object({ actif: z.boolean() }),
    whatsapp: z.object({ actif: z.boolean() }),
    slack: z.object({ actif: z.boolean(), webhookUrl: z.string().url().startsWith('https://hooks.slack.com/').optional() }),
  }),
  updatedAt: z.unknown(),
  updatedBy: z.string().min(1),
});
export type NotificationReglages = z.infer<typeof NotificationReglagesSchema>;

/** Un destinataire de regle. */
export const NotificationDestinataireSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('roles'), roles: z.array(z.enum(['owner', 'admin', 'member', 'viewer'])).min(1) }),
  z.object({ kind: z.literal('membres'), emails: z.array(z.string().trim().email()).min(1).max(200) }),
  z.object({ kind: z.literal('champ'), champ: chemin }),
]);
export type NotificationDestinataire = z.infer<typeof NotificationDestinataireSchema>;

/** Le gabarit : sujet et corps en `{{champ}}`, bouton optionnel. */
export const NotificationGabaritSchema = z.object({
  sujet: z.string().trim().min(1).max(NOTIFICATION_SUJET_MAX),
  corps: z.string().max(NOTIFICATION_CORPS_MAX),
  bouton: z.object({ libelle: z.string().trim().min(1).max(80), url: z.string().max(2048) }).optional(),
});

/** `orgs/{orgId}/notificationRules/{ruleId}`. Les champs de condition et de planification sont verifies par `superRefine`. */
export const NotificationRegleSchema = z
  .object({
    orgId: z.string().min(1),
    schemaVersion: z.literal(1),
    nom: z.string().trim().min(1).max(80),
    actif: z.boolean(),
    declencheur: z.enum(NOTIFICATION_DECLENCHEURS),
    famille: z.string().regex(/^[A-Za-z][A-Za-z0-9]{0,63}$/),
    type: z.string().min(1).max(64).optional(),
    condition: z.enum(NOTIFICATION_CONDITIONS).optional(),
    champ: chemin.optional(),
    valeur: z.string().max(200).optional(),
    champDate: chemin.optional(),
    decalageJours: z.number().int().min(-366).max(366).optional(),
    heure: heure.optional(),
    gabarit: NotificationGabaritSchema,
    destinataires: z.array(NotificationDestinataireSchema).min(1).max(20),
    canaux: z.array(z.enum(NOTIFICATION_CANAUX_CHOISISSABLES)).min(1),
    appId: z.string().min(1).max(64).optional(),
    porteId: z.string().min(1).max(64).optional(),
    createdAt: z.unknown(),
    updatedAt: z.unknown(),
    createdBy: z.string().min(1),
    updatedBy: z.string().min(1),
  })
  .superRefine((r, ctx) => {
    if (r.declencheur === 'evenement') {
      if (r.condition == null) ctx.addIssue({ code: 'custom', path: ['condition'], message: 'Une règle sur événement a une condition.' });
      if (r.condition !== 'cree' && r.champ == null) ctx.addIssue({ code: 'custom', path: ['champ'], message: 'Cette condition regarde un champ.' });
      if (r.condition === 'statutDevient' && r.valeur == null) ctx.addIssue({ code: 'custom', path: ['valeur'], message: 'Quelle valeur attendue ?' });
    }
    if (r.declencheur === 'planifie' && r.champDate == null) ctx.addIssue({ code: 'custom', path: ['champDate'], message: 'Une règle planifiée vise un champ date.' });
  });
export type NotificationRegle = z.infer<typeof NotificationRegleSchema>;

/** `orgs/{orgId}/notificationPrefs/{email}`. */
export const NotificationPrefsSchema = z.object({
  mascotteAnimee: z.boolean().optional(),
  notificationsExternes: NotificationsExternesSchema.optional(),
  orgId: z.string().min(1),
  email: z.string().email(),
  canaux: z.record(z.enum(NOTIFICATION_CANAUX_CHOISISSABLES), z.boolean()).optional(),
  silence: z.object({ de: heure, a: heure }).nullable().optional(),
  desinscrit: z.array(idSur).max(200).optional(),
  langue: z.enum(NOTIFICATION_LANGUES).optional(),
  updatedAt: z.unknown(),
  updatedBy: z.string().min(1),
});
export type NotificationPrefs = z.infer<typeof NotificationPrefsSchema>;

/** `orgs/{orgId}/notifications/{id}`, tel que l'ecran le LIT (le serveur seul l'ecrit). */
export const NotificationSchema = z.object({
  orgId: z.string(),
  schemaVersion: z.number(),
  canal: z.enum(NOTIFICATION_CANAUX_CHOISISSABLES),
  destinataire: z.string(),
  destinataireEmail: z.string().optional(),
  sujet: z.string(),
  corps: z.string(),
  bouton: z.object({ libelle: z.string(), url: z.string() }).optional(),
  replyTo: z.string().optional(),
  rubrique: z.enum(NOTIFICATION_RUBRIQUES).optional(),
  chemin: z.string().optional(),
  objet: z.object({ type: z.string(), id: z.string() }).optional(),
  luAt: z.unknown().optional(),
  domaine: z.enum(['conversation', 'horsConversation']).optional(),
  /** Le contexte d'une mention (socle conversations, contrat section 5) : conversation, message, type, ancre d'un chatter, libelle du lieu. */
  contexte: z.object({
    conversationId: z.string(),
    messageId: z.string(),
    type: z.enum(['canal', 'direct', 'chatter', 'page']),
    ancre: z.object({ type: z.string(), id: z.string() }).optional(),
    lieu: z.string().optional(),
  }).optional(),
  cleIdempotence: z.string(),
  source: z.object({ genre: z.enum(['regle', 'action', 'essai']), ruleId: z.string().optional(), famille: z.string().optional(), recordId: z.string().optional(), condition: z.string().optional(), acteur: z.string() }),
  statut: z.enum(NOTIFICATION_STATUTS),
  tentatives: z.array(z.object({ at: z.unknown(), canal: z.enum(NOTIFICATION_CANAUX), ok: z.boolean(), erreur: z.string().optional(), providerMessageId: z.string().optional() })),
  prochaineTentative: z.unknown(),
  providerMessageId: z.string().optional(),
  evenements: z.array(z.object({ at: z.unknown(), type: z.enum(NOTIFICATION_EVENEMENTS_LIVRAISON), detail: z.string().optional() })),
  derniereErreur: z.string().nullable().optional(),
  createdAt: z.unknown(),
  updatedAt: z.unknown(),
  createdBy: z.string(),
  updatedBy: z.string(),
});
export type Notification = z.infer<typeof NotificationSchema>;

/**
 * UN E-MAIL SIMULE : livre par le canal CONSOLE (emulateur sans les cles du
 * fournisseur), qui l'ecrit dans une boite d'envoi sur le disque au lieu de
 * l'envoyer. Le canal le marque deux fois, et l'un ou l'autre suffit : la
 * tentative porte `canal: 'console'`, et `providerMessageId` commence par
 * `console:`. Un ecran qui montre un tel message doit dire « simule, non
 * envoye », jamais « envoye ».
 */
export function estSimule(n: { providerMessageId?: string | undefined; tentatives: ReadonlyArray<{ canal: string; ok?: boolean }> }): boolean {
  return (n.providerMessageId?.startsWith(NOTIFICATION_PREFIXE_CONSOLE) ?? false)
    || n.tentatives.some((t) => t.canal === 'console');
}

/** Ce que `notifier` rend. */
export interface BilanNotifier {
  crees: number;
  existants: number;
  ecartes: Array<{ destinataire: string; canal: string; raison: string }>;
  ids: string[];
}

/* -------------------------------------------------------------------------- */
/* Le repository                                                               */
/* -------------------------------------------------------------------------- */

/** La region des Functions, la meme que `functions/src/runtime.ts` (gardee par `functions-region.test.mjs` via market-data). */
const NOTIFICATIONS_FUNCTIONS_REGION = 'europe-west1';

/** Les reglages tels que l'ecran les lit : jamais `null`, les defauts du serveur quand le document manque. */
export interface NotificationReglagesLus {
  existe: boolean;
  replyTo: string | null;
  nomExpediteur: string | null;
  piedDePage: string | null;
  langue: NotificationLangue | null;
  canaux: NotificationReglages['canaux'];
}

/** Ce que l'ecran envoie pour les reglages : champ absent = inchange, `null` = efface. */
export interface NotificationReglagesPatch {
  replyTo?: string | null;
  nomExpediteur?: string | null;
  piedDePage?: string | null;
  langue?: NotificationLangue | null;
  canaux?: Partial<{ email: boolean; whatsapp: boolean; slack: { actif: boolean; webhookUrl?: string | null } }>;
}

/** Une regle lue, avec son id. */
export interface NotificationRegleLue extends NotificationRegle {
  id: string;
}

/** Ce que l'ecran envoie pour creer ou modifier une regle (les champs standard sont poses par le SDK). */
export type NotificationRegleInput = Omit<NotificationRegle, 'orgId' | 'schemaVersion' | 'createdAt' | 'updatedAt' | 'createdBy' | 'updatedBy'>;

/** Les preferences de la personne connectee, jamais `null` (defauts quand le document manque). */
export interface NotificationPrefsLues {
  mascotteAnimee: boolean;
  notificationsExternes: NotificationsExternes;
  existe: boolean;
  canaux: Partial<Record<NotificationCanalChoisissable, boolean>>;
  silence: { de: string; a: string } | null;
  desinscrit: string[];
  langue: NotificationLangue | null;
}

export interface NotificationPrefsPatch {
  mascotteAnimee?: boolean;
  notificationsExternes?: NotificationsExternes;
  canaux?: Partial<Record<NotificationCanalChoisissable, boolean>>;
  silence?: { de: string; a: string } | null;
  desinscrit?: string[];
  langue?: NotificationLangue | null;
}

/** Un message du journal, avec son id et ses dates lues. */
export interface NotificationLue extends Omit<Notification, 'createdAt' | 'updatedAt' | 'prochaineTentative' | 'luAt'> {
  id: string;
  createdAt: Date | null;
  updatedAt: Date | null;
  prochaineTentative: Date | null;
  /** Boite interne : quand la personne l'a lue ; `null` = non lue. */
  luAt: Date | null;
}

/**
 * LES NON LUS de la boite interne de la personne connectee : le total et le
 * detail par rubrique, COMPTES PAR LE SERVEUR (`count()`, G15), jamais sur ce
 * qu'on a en memoire.
 */
export interface NonLusNotifications {
  total: number;
  parRubrique: Record<NotificationRubrique, number>;
}

/** Un message libre, tel que `envoyer` l'attend (voir `notifier` cote serveur). */
export interface NotificationMessageLibre {
  sujet: string;
  corps: string;
  bouton?: { libelle: string; url: string };
  emails?: string[];
  roles?: Array<'owner' | 'admin' | 'member' | 'viewer'>;
  canaux?: NotificationCanalChoisissable[];
  famille?: string;
  recordId?: string;
  cle?: string;
}

/** L'echec d'un appel au moteur, traduit. */
export class NotificationsError extends Error {
  readonly code: string;
  constructor(message: string, code: string) {
    super(message);
    this.name = 'NotificationsError';
    this.code = code;
  }
}

export interface NotificationsRepository {
  conversationsPrefs: { observer(recevoir: (prefs: PreferenceConversation[]) => void, erreur?: (e: unknown) => void): () => void; enregistrer(p: PreferenceConversation): Promise<void> };
  reglages: {
    lire(): Promise<NotificationReglagesLus>;
    observer(surChangement: (reglages: NotificationReglagesLus) => void, surErreur?: (erreur: unknown) => void): () => void;
    enregistrer(patch: NotificationReglagesPatch): Promise<void>;
  };
  regles: {
    list(): Promise<NotificationRegleLue[]>;
    observer(surChangement: (regles: NotificationRegleLue[]) => void, surErreur?: (erreur: unknown) => void): () => void;
    creer(input: NotificationRegleInput, id?: string): Promise<string>;
    modifier(id: string, patch: Partial<NotificationRegleInput>): Promise<void>;
    supprimer(id: string): Promise<void>;
  };
  prefs: {
    lire(): Promise<NotificationPrefsLues>;
    observer(surChangement: (prefs: NotificationPrefsLues) => void, surErreur?: (erreur: unknown) => void): () => void;
    enregistrer(patch: NotificationPrefsPatch): Promise<void>;
  };
  journal: {
    /** Les derniers messages de l'org (owner, admin). */
    list(options?: { limit?: number }): Promise<NotificationLue[]>;
    /** Les messages adresses a la personne connectee. */
    miens(options?: { limit?: number }): Promise<NotificationLue[]>;
    /**
     * Les messages de l'org crees depuis `depuis`, les plus recents en tete,
     * `limite` au plus (2000 par defaut et au plus) : la porte « Notifications »
     * du Journal (owner, admin).
     */
    periode(options: { depuis: Date; limite?: number }): Promise<NotificationLue[]>;
  };
  /** LA BOITE INTERNE de la personne connectee (la cloche, le centre de communication). */
  boite: {
    /** Suit les non lus : rappele a chaque changement, avec les comptes du serveur. Rend le desabonnement. */
    observerNonLus(surChangement: (nonLus: NonLusNotifications) => void, surErreur?: (erreur: unknown) => void): () => void;
    /** Les dernieres notifications internes, lues comprises, les plus recentes en tete. */
    lister(options?: { limit?: number; rubrique?: NotificationRubrique }): Promise<NotificationLue[]>;
    marquerLu(id: string): Promise<void>;
    /** Marque lues les notifications de cet objet (un sujet de Lia, une fiche) : a l'ouverture de sa conversation. */
    marquerLusDe(objet: { type: string; id: string }): Promise<number>;
    /** « Tout marquer lu », le seul geste global. */
    toutMarquerLu(): Promise<number>;
  };
  /** M'envoyer un essai sur un canal (e-mail par defaut). */
  envoyerEssai(canal?: NotificationCanalChoisissable): Promise<BilanNotifier>;
  /** Tester une regle sur une fiche (owner, admin). */
  testerRegle(ruleId: string, recordId: string): Promise<BilanNotifier>;
  /** Un message libre a des membres (bouton d'action, app). */
  envoyer(message: NotificationMessageLibre): Promise<BilanNotifier>;
  /** Rejouer la file et le passage planifie de l'org (owner, admin ; le seul moyen sous l'emulateur). */
  passage(): Promise<BilanNotifier & { regles?: number; sweeper?: Record<string, number> }>;
}

const MESSAGES_PAR_CODE: Record<string, string> = {
  'permission-denied': "Vous n'avez pas le droit de faire ce geste.",
  unauthenticated: 'Connectez-vous pour envoyer une notification.',
  'not-found': 'Cette fiche ou cette règle est introuvable.',
  'failed-precondition': "Le moteur n'a pas pu traiter la demande.",
  'invalid-argument': 'La demande est incomplète.',
  unavailable: 'Le moteur de notifications ne répond pas (Functions non déployées ?).',
  internal: 'Le moteur de notifications a échoué.',
};

function enErreur(cause: unknown): NotificationsError {
  const objet = typeof cause === 'object' && cause !== null ? cause : {};
  const code = String(Reflect.get(objet, 'code') ?? 'unknown').replace(/^functions\//, '');
  const message = String(Reflect.get(objet, 'message') ?? cause);
  return new NotificationsError(MESSAGES_PAR_CODE[code] ?? message, code);
}

function entier(valeur: unknown): number {
  return typeof valeur === 'number' && Number.isFinite(valeur) ? valeur : 0;
}

/** Lit la reponse de `notifier` sans lui faire confiance. Exportee pour le test. */
export function lireBilanNotifier(brut: unknown): BilanNotifier & { regles?: number; sweeper?: Record<string, number> } {
  const data = typeof brut === 'object' && brut !== null ? (brut as Record<string, unknown>) : {};
  const ecartes = Array.isArray(data['ecartes']) ? (data['ecartes'] as unknown[]) : [];
  const bilan: BilanNotifier & { regles?: number; sweeper?: Record<string, number> } = {
    crees: entier(data['crees']),
    existants: entier(data['existants']),
    ecartes: ecartes
      .filter((e): e is Record<string, unknown> => typeof e === 'object' && e !== null)
      .map((e) => ({ destinataire: String(e['destinataire'] ?? ''), canal: String(e['canal'] ?? ''), raison: String(e['raison'] ?? '') })),
    ids: Array.isArray(data['ids']) ? (data['ids'] as unknown[]).filter((i): i is string => typeof i === 'string') : [],
  };
  if (typeof data['regles'] === 'number') bilan.regles = data['regles'];
  if (typeof data['sweeper'] === 'object' && data['sweeper'] !== null) {
    bilan.sweeper = Object.fromEntries(Object.entries(data['sweeper'] as Record<string, unknown>).map(([k, v]) => [k, entier(v)]));
  }
  return bilan;
}

/** Zero non lu, par rubrique. */
export function nonLusVides(): NonLusNotifications {
  return { total: 0, parRubrique: Object.fromEntries(NOTIFICATION_RUBRIQUES.map((r) => [r, 0])) as Record<NotificationRubrique, number> };
}

function enDate(valeur: unknown): Date | null {
  if (valeur instanceof Date) return valeur;
  if (typeof valeur === 'object' && valeur !== null && typeof (valeur as { toDate?: unknown }).toDate === 'function') {
    return (valeur as { toDate: () => Date }).toDate();
  }
  return null;
}

/** Les defauts du serveur (`REGLAGES_PAR_DEFAUT` de `functions/src/notifications/contrat.ts`) : e-mail allume, le reste eteint. */
function reglagesDepuis(data: DocumentData | undefined): NotificationReglagesLus {
  const canaux = (data?.['canaux'] ?? {}) as Record<string, Record<string, unknown> | undefined>;
  const slack = canaux['slack'] ?? {};
  return {
    existe: data != null,
    replyTo: typeof data?.['replyTo'] === 'string' ? data['replyTo'] : null,
    nomExpediteur: typeof data?.['nomExpediteur'] === 'string' ? data['nomExpediteur'] : null,
    piedDePage: typeof data?.['piedDePage'] === 'string' ? data['piedDePage'] : null,
    langue: (NOTIFICATION_LANGUES as readonly string[]).includes(data?.['langue']) ? (data?.['langue'] as NotificationLangue) : null,
    canaux: {
      email: { actif: canaux['email']?.['actif'] !== false },
      whatsapp: { actif: canaux['whatsapp']?.['actif'] === true },
      slack: { actif: slack['actif'] === true, ...(typeof slack['webhookUrl'] === 'string' ? { webhookUrl: slack['webhookUrl'] } : {}) },
    },
  };
}

function prefsDepuis(data: DocumentData | undefined): NotificationPrefsLues {
  const verdict = NotificationPrefsSchema.partial().safeParse(data ?? {});
  const lu = verdict.success ? verdict.data : {};
  return {
    existe: data != null,
    mascotteAnimee: lu.mascotteAnimee ?? true,
    notificationsExternes: lu.notificationsExternes ?? {},
    canaux: lu.canaux ?? {},
    silence: lu.silence ?? null,
    desinscrit: lu.desinscrit ?? [],
    langue: lu.langue ?? null,
  };
}

function regleDepuis(id: string, data: DocumentData): NotificationRegleLue | null {
  const verdict = NotificationRegleSchema.safeParse(data);
  return verdict.success ? { id, ...verdict.data } : null;
}

function notificationDepuis(id: string, data: DocumentData): NotificationLue | null {
  const verdict = NotificationSchema.safeParse(data);
  if (!verdict.success) return null;
  const { createdAt, updatedAt, prochaineTentative, luAt, ...reste } = verdict.data;
  return { id, ...reste, createdAt: enDate(createdAt), updatedAt: enDate(updatedAt), prochaineTentative: enDate(prochaineTentative), luAt: enDate(luAt) };
}

const MOTIF_ID = /^[A-Za-z0-9_-]{1,128}$/;
const emulatorBoundFunctions = new WeakSet<object>();

/**
 * @param getActorId - l'uid de l'utilisateur connecte (les rules exigent `updatedBy == request.auth.uid`).
 * @param getActorEmail - son email en minuscules, l'id de SES preferences ; `null` hors connexion.
 * @param app - l'app Firebase, pour la callable `notifier`.
 * @param emulator - `config.emulators?.functions` en dev local.
 */
export function createNotificationsRepository(
  db: Firestore,
  getActorId: () => string,
  getActorEmail: () => string | null,
  orgId: string,
  app: FirebaseApp,
  emulator: EmulatorEndpoint | undefined,
): NotificationsRepository {
  const reglagesRef = doc(db, 'orgs', orgId, 'settings', NOTIFICATIONS_SETTINGS_ID);
  const reglesRef = collection(db, 'orgs', orgId, NOTIFICATION_REGLES_COLLECTION);
  const journalRef = collection(db, 'orgs', orgId, NOTIFICATIONS_COLLECTION);
  const prefsRef = (): ReturnType<typeof doc> => {
    const email = getActorEmail();
    if (email == null) throw new NotificationsError('Connectez-vous pour régler vos notifications.', 'unauthenticated');
    return doc(db, 'orgs', orgId, NOTIFICATION_PREFS_COLLECTION, email);
  };

  /** Les observateurs de non lus ouverts, a relancer apres un geste de lecture. */
  const recomptes = new Set<() => void>();
  const recompterTout = (): void => { for (const recompter of recomptes) recompter(); };

  /** La requete des non lus internes de cette personne. */
  function boiteNonLue(email: string) {
    return query(journalRef, where('destinataireEmail', '==', email), where('canal', '==', 'interne'), where('domaine', '==', 'horsConversation'), where('luAt', '==', null));
  }
  async function compterNonLus(email: string): Promise<NonLusNotifications> {
    const comptes = await Promise.all(
      NOTIFICATION_RUBRIQUES.map(async (rubrique) => [rubrique, (await getCountFromServer(query(boiteNonLue(email), where('rubrique', '==', rubrique)))).data().count] as const),
    );
    const parRubrique = Object.fromEntries(comptes) as Record<NotificationRubrique, number>;
    return { total: comptes.reduce((somme, [, n]) => somme + n, 0), parRubrique };
  }

  let appeler: ((payload: Record<string, unknown>) => Promise<{ data: unknown }>) | null = null;
  function callable(): (payload: Record<string, unknown>) => Promise<{ data: unknown }> {
    if (appeler !== null) return appeler;
    const functions = getFunctions(app, NOTIFICATIONS_FUNCTIONS_REGION);
    if (emulator && !emulatorBoundFunctions.has(functions)) {
      connectFunctionsEmulator(functions, emulator.host, emulator.port);
      emulatorBoundFunctions.add(functions);
    }
    appeler = httpsCallable<unknown, unknown>(functions, CALLABLE_NOTIFIER);
    return appeler;
  }
  async function notifier(payload: Record<string, unknown>) {
    try {
      const { data } = await callable()(payload);
      return lireBilanNotifier(data);
    } catch (cause) {
      throw enErreur(cause);
    }
  }

  function regleAEcrire(input: Partial<NotificationRegleInput>): Record<string, unknown> {
    const data: Record<string, unknown> = {};
    for (const [clef, valeur] of Object.entries(input)) {
      if (valeur === undefined) continue;
      data[clef] = valeur === null ? deleteField() : valeur;
    }
    return data;
  }

  return {
    conversationsPrefs: {
      observer(recevoir, erreur) {
        return onSnapshot(collection(prefsRef(), 'conversations'), snap => recevoir(snap.docs.flatMap(d => { const p = PreferenceConversationSchema.safeParse(d.data()); return p.success ? [p.data] : []; })), erreur);
      },
      async enregistrer(p) {
        const valide = PreferenceConversationSchema.parse(p);
        await setDoc(doc(prefsRef(), 'conversations', valide.ref), { ...valide, orgId, email: getActorEmail(), updatedAt: serverTimestamp(), updatedBy: getActorId() });
      },
    },
    reglages: {
      async lire() {
        const snap = await getDoc(reglagesRef);
        return reglagesDepuis(snap.exists() ? snap.data() : undefined);
      },
      observer(surChangement, surErreur) {
        return onSnapshot(reglagesRef, (snap) => surChangement(reglagesDepuis(snap.exists() ? snap.data() : undefined)), (erreur) => surErreur?.(erreur));
      },
      async enregistrer(patch) {
        const courant = await this.lire();
        const canaux = {
          email: { actif: patch.canaux?.email ?? courant.canaux.email.actif },
          whatsapp: { actif: patch.canaux?.whatsapp ?? courant.canaux.whatsapp.actif },
          slack: (() => {
            const actif = patch.canaux?.slack?.actif ?? courant.canaux.slack.actif;
            const url = patch.canaux?.slack === undefined || patch.canaux.slack.webhookUrl === undefined ? courant.canaux.slack.webhookUrl : patch.canaux.slack.webhookUrl;
            return url ? { actif, webhookUrl: url } : { actif };
          })(),
        };
        const uid = getActorId();
        const futur: Record<string, unknown> = { orgId, schemaVersion: 1, canaux, updatedAt: new Date(), updatedBy: uid };
        const document: Record<string, unknown> = { orgId, schemaVersion: 1, canaux, updatedAt: serverTimestamp(), updatedBy: uid };
        for (const clef of ['replyTo', 'nomExpediteur', 'piedDePage', 'langue'] as const) {
          const valeur = patch[clef];
          if (valeur === undefined) {
            if (courant[clef] != null) futur[clef] = courant[clef];
            continue;
          }
          if (valeur === null || valeur === '') document[clef] = deleteField();
          else {
            document[clef] = valeur;
            futur[clef] = valeur;
          }
        }
        const verdict = NotificationReglagesSchema.safeParse(futur);
        if (!verdict.success) throw new NotificationsError(verdict.error.issues.map((i) => `${i.path.join('.')} : ${i.message}`).join(' ; '), 'invalid-argument');
        await setDoc(reglagesRef, document, { merge: true });
      },
    },
    regles: {
      async list() {
        const snap = await getDocs(query(reglesRef, orderBy('nom')));
        return snap.docs.map((d) => regleDepuis(d.id, d.data())).filter((r): r is NotificationRegleLue => r != null);
      },
      observer(surChangement, surErreur) {
        return onSnapshot(query(reglesRef, orderBy('nom')), (snap) => surChangement(snap.docs.map((d) => regleDepuis(d.id, d.data())).filter((r): r is NotificationRegleLue => r != null)), (erreur) => surErreur?.(erreur));
      },
      async creer(input, id) {
        const ruleId = id ?? doc(reglesRef).id;
        if (!MOTIF_ID.test(ruleId)) throw new NotificationsError('Identifiant de règle invalide.', 'invalid-argument');
        const uid = getActorId();
        const document = { ...input, orgId, schemaVersion: 1 as const, createdAt: new Date(), updatedAt: new Date(), createdBy: uid, updatedBy: uid };
        const verdict = NotificationRegleSchema.safeParse(document);
        if (!verdict.success) throw new NotificationsError(verdict.error.issues.map((i) => `${i.path.join('.')} : ${i.message}`).join(' ; '), 'invalid-argument');
        await setDoc(doc(reglesRef, ruleId), { ...regleAEcrire(input), orgId, schemaVersion: 1, createdAt: serverTimestamp(), updatedAt: serverTimestamp(), createdBy: uid, updatedBy: uid });
        return ruleId;
      },
      async modifier(id, patch) {
        const snap = await getDoc(doc(reglesRef, id));
        if (!snap.exists()) throw new NotificationsError('Cette règle est introuvable.', 'not-found');
        const futur = { ...snap.data(), ...Object.fromEntries(Object.entries(patch).filter(([, v]) => v !== undefined && v !== null)) };
        for (const [clef, valeur] of Object.entries(patch)) if (valeur === null) delete futur[clef];
        const verdict = NotificationRegleSchema.safeParse({ ...futur, createdAt: new Date(), updatedAt: new Date() });
        if (!verdict.success) throw new NotificationsError(verdict.error.issues.map((i) => `${i.path.join('.')} : ${i.message}`).join(' ; '), 'invalid-argument');
        await updateDoc(doc(reglesRef, id), { ...regleAEcrire(patch), updatedAt: serverTimestamp(), updatedBy: getActorId() });
      },
      async supprimer(id) {
        await deleteDoc(doc(reglesRef, id));
      },
    },
    prefs: {
      async lire() {
        const snap = await getDoc(prefsRef());
        return prefsDepuis(snap.exists() ? snap.data() : undefined);
      },
      observer(surChangement, surErreur) {
        let ref: ReturnType<typeof doc>;
        try {
          ref = prefsRef();
        } catch (erreur) {
          surErreur?.(erreur);
          return () => {};
        }
        return onSnapshot(ref, (snap) => surChangement(prefsDepuis(snap.exists() ? snap.data() : undefined)), (erreur) => surErreur?.(erreur));
      },
      async enregistrer(patch) {
        const email = getActorEmail();
        if (email == null) throw new NotificationsError('Connectez-vous pour régler vos notifications.', 'unauthenticated');
        const document: Record<string, unknown> = { orgId, email, updatedAt: serverTimestamp(), updatedBy: getActorId() };
        if (patch.mascotteAnimee !== undefined) document['mascotteAnimee'] = patch.mascotteAnimee;
        if (patch.notificationsExternes !== undefined) document['notificationsExternes'] = patch.notificationsExternes;
        if (patch.canaux !== undefined) document['canaux'] = patch.canaux;
        if (patch.silence !== undefined) document['silence'] = patch.silence;
        if (patch.desinscrit !== undefined) document['desinscrit'] = patch.desinscrit;
        if (patch.langue !== undefined) document['langue'] = patch.langue === null ? deleteField() : patch.langue;
        const verdict = NotificationPrefsSchema.safeParse({ ...document, langue: patch.langue ?? undefined, updatedAt: new Date() });
        if (!verdict.success) throw new NotificationsError(verdict.error.issues.map((i) => i.message).join(' ; '), 'invalid-argument');
        await setDoc(prefsRef(), document, { merge: true });
      },
    },
    journal: {
      async list(options) {
        const snap = await getDocs(query(journalRef, orderBy('createdAt', 'desc'), limiteDe(Math.min(options?.limit ?? 50, 200))));
        return snap.docs.map((d) => notificationDepuis(d.id, d.data())).filter((n): n is NotificationLue => n != null);
      },
      async periode(options) {
        const limite = Math.max(1, Math.min(options.limite ?? NOTIFICATION_PERIODE_MAX, NOTIFICATION_PERIODE_MAX));
        const snap = await getDocs(query(journalRef, where('createdAt', '>=', options.depuis), orderBy('createdAt', 'desc'), limiteDe(limite)));
        return snap.docs.map((d) => notificationDepuis(d.id, d.data())).filter((n): n is NotificationLue => n != null);
      },
      async miens(options) {
        const email = getActorEmail();
        if (email == null) return [];
        const snap = await getDocs(query(journalRef, where('destinataireEmail', '==', email), orderBy('createdAt', 'desc'), limiteDe(Math.min(options?.limit ?? 20, 100))));
        return snap.docs.map((d) => notificationDepuis(d.id, d.data())).filter((n): n is NotificationLue => n != null);
      },
    },
    boite: {
      observerNonLus(surChangement, surErreur) {
        const email = getActorEmail();
        if (email == null) {
          surChangement(nonLusVides());
          return () => {};
        }
        let vivant = true;
        let enCours = 0;
        // Le SIGNAL vient d'un abonnement sur les non lus (borne) ; les NOMBRES,
        // de `count()` cote serveur, par rubrique : jamais une longueur de page.
        const recompter = (): void => {
          const tour = ++enCours;
          void compterNonLus(email)
            .then((nonLus) => { if (vivant && tour === enCours) surChangement(nonLus); })
            .catch((erreur) => surErreur?.(erreur));
        };
        // Une notification marquee lue ICI sort de la requete : l'instantane
        // part sur l'ecriture LOCALE, avant que le serveur l'ait recue, et un
        // `count()` lance a ce moment-la lirait l'ancien etat ; la confirmation
        // du serveur, elle, ne redeclenche rien. Les gestes de lecture
        // relancent donc le compte apres leur ecriture (`recomptes`).
        recomptes.add(recompter);
        const arreter = onSnapshot(query(boiteNonLue(email), orderBy('updatedAt', 'desc'), limiteDe(1)), recompter, (erreur) => surErreur?.(erreur));
        // Une lecture sur un autre poste peut viser un document hors du signal d'arrivee.
        const lectures = onSnapshot(query(journalRef, where('destinataireEmail', '==', email), where('canal', '==', 'interne'), where('domaine', '==', 'horsConversation'), orderBy('luAt', 'desc'), limiteDe(1)), { includeMetadataChanges: true }, snap => { if (!snap.metadata.hasPendingWrites && !snap.metadata.fromCache) recompter(); }, (erreur) => surErreur?.(erreur));
        return () => { vivant = false; recomptes.delete(recompter); arreter(); lectures(); };
      },
      async lister(options) {
        const email = getActorEmail();
        if (email == null) return [];
        const filtres = [where('destinataireEmail', '==', email), where('canal', '==', 'interne'), where('domaine', '==', 'horsConversation')];
        if (options?.rubrique) filtres.push(where('rubrique', '==', options.rubrique));
        const snap = await getDocs(query(journalRef, ...filtres, orderBy('createdAt', 'desc'), limiteDe(Math.min(options?.limit ?? 30, 100))));
        return snap.docs.map((d) => notificationDepuis(d.id, d.data())).filter((n): n is NotificationLue => n != null);
      },
      async marquerLu(id) {
        if (!MOTIF_ID.test(id)) throw new NotificationsError('Identifiant de notification invalide.', 'invalid-argument');
        await updateDoc(doc(journalRef, id), { luAt: serverTimestamp() });
        recompterTout();
      },
      async marquerLusDe(objet) {
        const email = getActorEmail();
        if (email == null) return 0;
        const snap = await getDocs(query(boiteNonLue(email), where('objet', '==', objet), limiteDe(100)));
        await Promise.all(snap.docs.map((d) => updateDoc(d.ref, { luAt: serverTimestamp() })));
        recompterTout();
        return snap.size;
      },
      async toutMarquerLu() {
        const email = getActorEmail();
        if (email == null) return 0;
        const snap = await getDocs(query(boiteNonLue(email), limiteDe(200)));
        await Promise.all(snap.docs.map((d) => updateDoc(d.ref, { luAt: serverTimestamp() })));
        recompterTout();
        return snap.size;
      },
    },
    envoyerEssai: (canal) => notifier({ essai: true, ...(canal ? { canal } : {}) }),
    testerRegle: (ruleId, recordId) => notifier({ ruleId, recordId }),
    envoyer: (message) => notifier({ message }),
    passage: () => notifier({ passage: true }),
  };
}
